/** Coupons (admin console): the create form's draft and what a coupon does, in words. Pure. */
import type { S } from "../api/client";
import type { Translate } from "../i18n/I18nProvider";
import { parseLux } from "./lux.ts";

export type CouponDraft = {
  code: string;
  name: string;
  applies_to: "subscription" | "lux";
  effect: "percent" | "amount" | "bonus";
  amount: string;
  /** Bonus Lux: whole months it lasts after the purchase (1 to 60; default 12). */
  bonus_months: string;
  duration: "once" | "repeating" | "forever";
  months: string;
  plans: string[];
  billings: string[];
  valid_from: string; // datetime-local
  valid_until: string;
  max_redemptions: string;
  per_company_limit: string;
  stackable: boolean;
};

export function emptyCouponDraft(): CouponDraft {
  return {
    code: "", name: "", applies_to: "subscription", effect: "percent", amount: "", bonus_months: "12", duration: "once", months: "",
    plans: [], billings: [], valid_from: "", valid_until: "", max_redemptions: "", per_company_limit: "1", stackable: true,
  };
}

const int = (s: string): number | null => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : null);
const iso = (local: string): string | null => (local ? new Date(local).toISOString() : null);

/** The POST body for a draft (the API checks the rest). */
export function couponWriteBody(d: CouponDraft): S["CouponWrite"] {
  const plain = d.amount.replace(/[$,%\s]/g, "");
  const percent = /^\d+(\.\d+)?$/.test(plain) ? Number(plain) : null;
  // dollars and Lux both have two decimals: parsed as integers ("19.99" → 1999), never through a float × 100
  const hundredths = parseLux(plain) ?? null;
  const body: S["CouponWrite"] = {
    code: d.code.trim().toUpperCase(), name: d.name.trim(), applies_to: d.applies_to,
    percent_off: d.effect === "percent" ? percent : null,
    amount_off_cents: d.effect === "amount" ? hundredths : null,
    bonus_lux: d.effect === "bonus" ? hundredths : null,
    ...(d.effect === "bonus" ? { bonus_expiry_months: int(d.bonus_months) ?? 12 } : {}),
    valid_from: iso(d.valid_from), valid_until: iso(d.valid_until),
    max_redemptions: int(d.max_redemptions), per_company_limit: int(d.per_company_limit), stackable: d.stackable,
  };
  if (d.applies_to === "subscription") {
    body.duration = d.duration;
    body.duration_months = d.duration === "repeating" ? int(d.months) : null;
    body.plans = d.plans as S["CouponWrite"]["plans"];
    body.billings = d.billings as S["CouponWrite"]["billings"];
  }
  return body;
}

/** The formatters `couponEffect` needs (the admin console's `useFmt()` gives them). */
export type CouponFmt = {
  cents: (minor: number, currency?: string) => string;
  lux: (hundredths: number) => string;
  percent: (value: number) => string;
};

/** What a coupon does, as a translated sentence: "20% off for 3 months", "$50.00 off the first invoice",
 * "25.00 bonus Lux, lasts 12 months", "15% off a Lux pack". */
export function couponEffect(
  c: Pick<S["Coupon"], "applies_to" | "percent_off" | "amount_off_cents" | "bonus_lux" | "duration" | "duration_months"> &
    Partial<Pick<S["Coupon"], "bonus_expiry_months">>,
  t: Translate,
  fmt: CouponFmt,
): string {
  if (c.bonus_lux) return t("admin.coupons.effect.bonusLux", { lux: fmt.lux(c.bonus_lux), months: c.bonus_expiry_months ?? 12 });
  const discount = c.percent_off ? fmt.percent(Number(c.percent_off)) : fmt.cents(c.amount_off_cents ?? 0, "USD");
  if (c.applies_to === "lux") return t("admin.coupons.effect.luxPack", { discount });
  if (c.duration === "forever") return t("admin.coupons.effect.forever", { discount });
  if (c.duration === "repeating") return t("admin.coupons.effect.repeating", { discount, months: c.duration_months ?? 0 });
  return t("admin.coupons.effect.once", { discount });
}
